import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import AddCageModal from '@/components/molecules/AddCageModal/AddCageModal';
import ContentBlock from '@/components/molecules/ContentBlock';
import { Disclosure } from '@/components/molecules/Disclosure';
import EditCageModal, { type EditableCage } from '@/components/molecules/EditCageModal';
import SomethingHappened from '@/components/SomethingHappened';
import { useSearchAquacultureSites } from '@/domain/aquacultureSites';
import type { LiceType, LocationTypeInfo } from '@/domain/cages';

import type { ClientLocationProps, ClientProps } from '../Client';

import AddLocationModal from './AddLocationModal';
import DeleteLocationConfirm from './DeleteLocationConfirm';
import LocationsTable from './LocationsTable';
import type { AddNewLocationInput } from './locationFormValues';

interface ClientLocationsProps
    extends Omit<
        ClientProps,
        'apiUsers' | 'createApiUser' | 'addCreatedApiUser' | 'clientLocations'
    > {}

function getInitialLocationState(locations: ClientLocationProps[]): Record<number, boolean> {
    return locations.reduce<Record<number, boolean>>((state, location) => {
        state[location.id] = false;
        return state;
    }, {});
}

function isLocationActive(location: ClientLocationProps): boolean {
    return location.cages.some(
        (cage) => cage.historical == null && cage.current_bioscope?.bioscopeId != null
    );
}

function sortByLocationName(a: ClientLocationProps, b: ClientLocationProps): number {
    return a.name.localeCompare(b.name, undefined, { numeric: true });
}

function ClientLocations({
    cageTypesList = [],
    fishTypesList = [],
    liceTypesList = [] as LiceType[],
    locationTypesList = [] as LocationTypeInfo[],
    typesLoadError,
    client,
    updateLocation,
    createManyCages,
    createNewLocation,
    updateCage,
    refetch,
}: ClientLocationsProps) {
    const { t } = useTranslation();
    const { locations } = client;
    const [expanded, setExpanded] = useState<Record<number, boolean>>(() =>
        getInitialLocationState(locations)
    );

    const [selectedLocationForModal, setSelectedLocationForModal] = useState<number | undefined>(
        undefined
    );
    const [locationForUpdate, setLocationForUpdate] = useState<ClientLocationProps | null>(null);
    // The pen being edited, plus the location it sits at: the modal needs the location for its
    // allowed pen types and for the species a pen without one of its own inherits.
    const [cageForEdit, setCageForEdit] = useState<{
        cage: EditableCage;
        location: ClientLocationProps;
    } | null>(null);
    const [addLocationModalIsOpen, setAddLocationModalIsOpen] = useState(false);
    // The modify form carries the id of the location it was opened with.
    const updateExistingLocation = (input: AddNewLocationInput) =>
        input.id === undefined
            ? Promise.reject(new Error('A location update needs the location id'))
            : updateLocation({ ...input, id: input.id });
    const [updateLocationModalIsOpen, setUpdateLocationModalIsOpen] = useState(false);
    const [deleteLocationModalIsOpen, setDeleteLocationModalIsOpen] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<Record<number, boolean>>(() =>
        getInitialLocationState(locations)
    );

    const { activeLocations, inactiveLocations } = useMemo(() => {
        const activeLocations: ClientLocationProps[] = [];
        const inactiveLocations: ClientLocationProps[] = [];

        for (const location of locations) {
            if (isLocationActive(location)) {
                activeLocations.push(location);
            } else {
                inactiveLocations.push(location);
            }
        }

        return {
            activeLocations: activeLocations.sort(sortByLocationName),
            inactiveLocations: inactiveLocations.sort(sortByLocationName),
        };
    }, [locations]);

    const fetchAquaCultureSites = useSearchAquacultureSites();
    const toggleLocationExpanded = (locationId: number, isExpanded: boolean) => {
        setExpanded((previous) => ({
            ...previous,
            [locationId]: isExpanded,
        }));
    };

    const toggleLocationSelected = (locationId: number, isSelected: boolean) => {
        setSelectedLocations((previous) => ({
            ...previous,
            [locationId]: isSelected,
        }));
    };

    const toggleSelectedLocations = (locationIds: number[], isSelected: boolean) => {
        setSelectedLocations((previous) => ({
            ...previous,
            ...locationIds.reduce<Record<number, boolean>>((selected, locationId) => {
                selected[locationId] = isSelected;
                return selected;
            }, {}),
        }));
    };

    const handleAddCageToLocation = useCallback(
        (locationId: number) => {
            setLocationForUpdate(locations.find((location) => location.id === locationId) ?? null);
            setSelectedLocationForModal(locationId);
        },
        [locations]
    );

    const handleDeleteCageToLocation = useCallback(
        (locationId: number) => {
            setLocationForUpdate(locations.find((location) => location.id === locationId) ?? null);
            setDeleteLocationModalIsOpen(true);
        },
        [locations]
    );

    const handleEditCage = useCallback((cage: EditableCage, location: ClientLocationProps) => {
        setCageForEdit({ cage, location });
    }, []);

    const handleUpdateLocation = useCallback((location: ClientLocationProps) => {
        setLocationForUpdate(location);
        setUpdateLocationModalIsOpen(true);
    }, []);

    const locationsTableProps = {
        expandedLocations: expanded,
        selectedLocations,
        refetch,
        onAddCageToLocation: handleAddCageToLocation,
        onDeleteCageToLocation: handleDeleteCageToLocation,
        onUpdateLocation: handleUpdateLocation,
        onEditCage: handleEditCage,
        onToggleLocationExpanded: toggleLocationExpanded,
        onToggleLocationSelected: toggleLocationSelected,
        onToggleLocationsSelected: toggleSelectedLocations,
    };

    if (typesLoadError) {
        return (
            <ContentBlock title={t('common.labels.location')}>
                <SomethingHappened
                    title={t('internal.client.typesLoadError')}
                    {...typesLoadError}
                />
            </ContentBlock>
        );
    }

    return (
        <ContentBlock title={t('common.labels.location')}>
            <Disclosure title={t('common.labels.activeLocations')} defaultOpen>
                <LocationsTable locations={activeLocations} {...locationsTableProps} />
            </Disclosure>
            <Disclosure title={t('common.labels.inactiveLocations')} spacing="top-and-bottom">
                <LocationsTable locations={inactiveLocations} {...locationsTableProps} />
            </Disclosure>
            <Button onClick={() => setAddLocationModalIsOpen(true)}>
                {t('internal.client.addLocation')}
            </Button>

            <AddLocationModal
                key={'new'}
                isOpen={addLocationModalIsOpen}
                fetchAquaCultureSites={fetchAquaCultureSites}
                onSubmit={createNewLocation}
                onClose={() => setAddLocationModalIsOpen(false)}
                modifyOrNew={'new'}
                fishTypesList={fishTypesList}
                liceTypesList={liceTypesList}
                locationTypesList={locationTypesList}
            />
            <AddCageModal
                location={locations.find((location) => location.id === selectedLocationForModal)}
                cageTypesList={cageTypesList}
                fishTypesList={fishTypesList}
                locationId={selectedLocationForModal}
                createManyCages={createManyCages}
                onClose={() => setSelectedLocationForModal(undefined)}
            />
            {locationForUpdate && (
                <AddLocationModal
                    isOpen={updateLocationModalIsOpen}
                    fishTypesList={fishTypesList}
                    liceTypesList={liceTypesList}
                    locationTypesList={locationTypesList}
                    key={locationForUpdate.id}
                    fetchAquaCultureSites={fetchAquaCultureSites}
                    onSubmit={updateExistingLocation}
                    onClose={() => setUpdateLocationModalIsOpen(false)}
                    modifyOrNew={'modify'}
                    initialState={locationForUpdate}
                />
            )}

            {cageForEdit && (
                <EditCageModal
                    key={cageForEdit.cage.id}
                    cage={cageForEdit.cage}
                    location={cageForEdit.location}
                    cageTypesList={cageTypesList}
                    fishTypesList={fishTypesList}
                    updateCage={updateCage}
                    onClose={() => setCageForEdit(null)}
                />
            )}

            <DeleteLocationConfirm
                location={locationForUpdate}
                isOpen={deleteLocationModalIsOpen}
                onClose={() => setDeleteLocationModalIsOpen(false)}
                refetch={refetch}
            />
        </ContentBlock>
    );
}

export default ClientLocations;
